import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { INVENTORY_API_URL, DEFAULT_LIST_PAGE_SIZE } from '../constants';
import { InventoryGrid, type InventoryGridRow } from '../components/grids/InventoryGrid';

type PageDto = { items: InventoryGridRow[]; totalCount: number; page: number; pageSize: number };

export function InventoryPage(props: {
  authFetch: (url: string) => Promise<Response>;
  selectedInventoryItemId: string | null;
  onSelectInventoryItem: (id: string | null) => void;
}): ReactElement {
  const { authFetch, selectedInventoryItemId, onSelectInventoryItem } = props;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [data, setData] = useState<PageDto | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [kindInput, setKindInput] = useState('');
  const [skuInput, setSkuInput] = useState('');
  const [kindDebounced, setKindDebounced] = useState('');
  const [skuDebounced, setSkuDebounced] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setKindDebounced(kindInput), 350);
    return () => window.clearTimeout(t);
  }, [kindInput]);

  useEffect(() => {
    const t = window.setTimeout(() => setSkuDebounced(skuInput), 350);
    return () => window.clearTimeout(t);
  }, [skuInput]);

  useEffect(() => {
    setPage(1);
  }, [kindDebounced, skuDebounced]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const u = new URL(INVENTORY_API_URL);
    u.searchParams.set('page', String(page));
    u.searchParams.set('pageSize', String(pageSize));
    const k = kindDebounced.trim();
    const s = skuDebounced.trim();
    if (k) {
      u.searchParams.set('materialLineKind', k);
    }
    if (s) {
      u.searchParams.set('sku', s);
    }
    try {
      const res = await authFetch(u.toString());
      if (!res.ok) {
        setData(null);
        setErr('Could not load inventory.');
        return;
      }
      const json = (await res.json()) as PageDto;
      setData(json);
    } catch {
      setData(null);
      setErr('Could not load inventory.');
    } finally {
      setLoading(false);
    }
  }, [authFetch, page, pageSize, kindDebounced, skuDebounced]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <InventoryGrid
      error={err}
      rows={data?.items ?? []}
      loading={loading}
      selectedId={selectedInventoryItemId}
      onSelectRow={onSelectInventoryItem}
      page={data?.page ?? page}
      pageSize={data?.pageSize ?? pageSize}
      totalCount={data?.totalCount ?? 0}
      onPageChange={setPage}
      onPageSizeChange={(n) => {
        setPage(1);
        setPageSize(n);
      }}
      materialLineKindFilter={kindInput}
      onMaterialLineKindFilterChange={setKindInput}
      skuFilter={skuInput}
      onSkuFilterChange={setSkuInput}
    />
  );
}
